/**
 * Pure helpers for ActivityDetailDialogNew — extracted so the date/id/note
 * resolution logic can be unit-tested without mounting the dialog.
 */
import moment from 'moment'

// Local copy of the activity-state labels (kept in sync manually with the
// legacy list in src/components/templates/ActivityDialog/ActivityDialog.tsx
// on purpose — the new-style tree must not import from the legacy one). Fixes
// a trailing-space typo on 'TO_BE_CONCLUDED ' present in the legacy list,
// which made that state never match and always render blank there.
export const ACTIVITY_STATES: {value: string; label: string; color: 'success' | 'warning' | 'error' | 'default'}[] = [
  {value: 'TO_BE_PLANNED', label: 'Da pianificare', color: 'default'},
  {value: 'TO_BE_CONFIRMED', label: 'In attesa di conferma', color: 'warning'},
  {value: 'TO_BE_CONCLUDED', label: 'In corso', color: 'warning'},
  {value: 'TO_BE_VERIFIED', label: 'Confermata', color: 'warning'},
  {value: 'COMPLETED', label: 'Completata', color: 'success'},
  {value: 'CANCELLED', label: 'Annullata', color: 'error'},
]

/**
 * Computes "Data ora fine" by adding the reported activity duration (in
 * minutes) to "Data ora inizio". Returns undefined when either input is
 * missing, so the caller can fall back to the ReadOnlyField placeholder.
 */
export function computeActivityEndDate(
  plannedDate?: string,
  durationMinutes?: number
): string | undefined {
  if (!plannedDate || durationMinutes == null) return undefined
  const start = moment(plannedDate)
  if (!start.isValid()) return undefined
  return start.add(durationMinutes, 'minutes').toISOString()
}

/**
 * When the activity was completed: the timestamp of its latest transition to
 * COMPLETED (activities-service pushes one into statesTransitions when the
 * operator closes it). Undefined when there's none, e.g. not completed yet.
 */
export function getActivityCompletionDate(
  statesTransitions?: {state?: string; timestamp?: string}[]
): string | undefined {
  const completions = (statesTransitions ?? []).filter(t => t.state === 'COMPLETED' && t.timestamp)
  return completions.length > 0 ? completions[completions.length - 1].timestamp : undefined
}

// The sub-activity carrying the activity description has a generated
// typeCode (`params-collection-<random>`), so it can't be matched by
// equality — it's recognized by prefix + the presence of its own feedback.
const ACTIVITY_DESCRIPTION_TYPE_CODE_PREFIX = 'params-collection-'
const ACTIVITY_DESCRIPTION_FEEDBACK_ID = 'descrizioneAttivita'

function findActivityDescriptionFeedback(instance?: Record<string, any>): any | undefined {
  if (!instance?.typeCode?.startsWith(ACTIVITY_DESCRIPTION_TYPE_CODE_PREFIX)) return undefined
  return instance.feedbacks?.find((feedback: any) => feedback?.id === ACTIVITY_DESCRIPTION_FEEDBACK_ID)
}

/**
 * Finds the `params-collection-*` sub-activity instance among the ones
 * fetched for an activity's `subactivityIds`, and returns the value of its
 * `descrizioneAttivita` feedback. Returns undefined when no such
 * instance/feedback exists.
 */
export function extractActivityDescription(instances?: Record<string, any>[]): string | undefined {
  for (const instance of instances ?? []) {
    const feedback = findActivityDescriptionFeedback(instance)
    if (feedback) return feedback.value
  }
  return undefined
}

/**
 * Sub-activity ("compito") formatting — ported from the Word export
 * algorithm (Backend/therapy-service/.../assumptions/export/handler.js) so
 * the same rules (exclusions, no-param types, feedback formatting) apply in
 * the activity detail modal, producing structured rows instead of XML.
 */

export interface SubactivityParamLine {
  label?: string
  value: string
}

// A complex-type feedback (e.g. "Pre-infusione" / "Post-infusione") renders
// its own labeled group of param lines, so its values aren't flattened into
// the surrounding grid and can't be confused with another group's.
export interface SubactivityParamGroup {
  label?: string
  lines: SubactivityParamLine[]
}

export interface SubactivityView {
  id: string
  title: string
  isOk: boolean
  lines: SubactivityParamLine[]
  groups: SubactivityParamGroup[]
}

// Sub-activities that must never surface as a "compito" — the sign-report is
// shown elsewhere, and the params-collection instance carrying the activity
// description already feeds the "Descrizione attività" section above (see
// isExcludedSubactivity, which additionally excludes it by feedback match).
const EXCLUDED_TYPE_CODES = ['sign-report']
const EXCLUDED_TITLES = ['Prossima attività programmata', 'Prossima infusione programmata']

// Sub-activity types with no meaningful feedback to display — only the
// title + status are rendered for these.
const NO_PARAM_TYPE_CODES = [
  'no-params', 'device-collection', 'device-delivery', 'drug-delivery',
  'drug-collection', 'material-delivery', 'eco-exam', 'eco-report',
  'eco-exam-and-report', 'images-collection',
]

function isExcludedSubactivity(instance: Record<string, any>): boolean {
  return EXCLUDED_TYPE_CODES.includes(instance?.typeCode)
    || EXCLUDED_TITLES.includes(instance?.title)
    || Boolean(findActivityDescriptionFeedback(instance))
}

function hasNoParams(instance: Record<string, any>): boolean {
  return NO_PARAM_TYPE_CODES.some((code) => instance?.typeCode?.includes(code))
}

const IT_DATE_FORMAT = {year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Rome'} as const
const IT_TIME_FORMAT = {hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome'} as const

/**
 * Formats a complex-type feedback's own values into param lines. The
 * instance's own feedback carries no field labels — each value is matched
 * by id against the complex-type entry's `fields` in the feedbacksFormat
 * catalog (falls back to positional `feedback.fields` for instances that do
 * embed it directly).
 */
function buildComplexParamLines(feedback: any, complexFormat?: any): SubactivityParamLine[] {
  return (feedback.value ?? [])
    .map((val: any, index: number) => {
      if (val?.value === null || val?.value === undefined || val?.value === '') return null
      const label = complexFormat?.fields?.find((field: any) => field.id === val.id)?.label
        ?? feedback.fields?.[index]?.label
      const value = val.unitMeasure ? `${val.value} ${val.unitMeasure}` : `${val.value}`
      return {label, value}
    })
    .filter(Boolean)
}

/** Formats a single non-complex feedback into zero or one label/value param line. */
function buildSimpleParamLines(feedback: any, feedbacksFormat?: any[]): SubactivityParamLine[] {
  if (feedback?.value === null || feedback?.value === undefined || feedback?.value === '') return []

  const label = feedbacksFormat?.find((format: any) => format.id === feedback.id)?.label

  if (feedback.unitMeasure) {
    return [{label, value: `${feedback.value} ${feedback.unitMeasure}`}]
  }
  if (feedback.type === 'date') {
    return [{label, value: new Date(feedback.value).toLocaleDateString('it-IT', IT_DATE_FORMAT)}]
  }
  if (feedback.type === 'hours') {
    return [{label, value: new Date(feedback.value).toLocaleTimeString('it-IT', IT_TIME_FORMAT)}]
  }
  return [{label, value: `${feedback.value}`}]
}

/**
 * Returns the distinct sub-activity type codes for which the feedback
 * labels catalog (feedbacksFormat, via getFeedbackConfig) is actually
 * needed — i.e. sub-activities that are shown, completed, and not in
 * NO_PARAM_TYPE_CODES. Lets the caller avoid fetching config for types
 * whose feedbacks are never rendered.
 */
export function getFeedbackTypeCodes(instances?: Record<string, any>[]): string[] {
  const codes = (instances ?? [])
    .filter((instance) => !isExcludedSubactivity(instance) && instance?.isOk && !hasNoParams(instance))
    .map((instance) => instance.typeCode)
    .filter(Boolean)
  return [...new Set(codes)]
}

/**
 * Builds the "Compiti" view model for an activity's sub-activities: title,
 * Avvenuto/Non avvenuto status, and — when applicable — the formatted
 * feedback/motivation param lines. `feedbackConfigByType` maps a typeCode to
 * its feedbacksFormat (the response of apiClient.getFeedbackConfig).
 */
export function buildSubactivitiesView(
  instances?: Record<string, any>[],
  feedbackConfigByType: Record<string, any[]> = {}
): SubactivityView[] {
  return (instances ?? [])
    .filter((instance) => !isExcludedSubactivity(instance))
    .map((instance) => {
      const id = instance._id ?? `${instance.typeCode}:${instance.title}`
      const isOk = Boolean(instance.isOk)

      if (!isOk) {
        const lines = instance.motivation ? [{label: 'Motivazione', value: instance.motivation}] : []
        return {id, title: instance.title, isOk, lines, groups: []}
      }

      if (hasNoParams(instance)) {
        return {id, title: instance.title, isOk, lines: [], groups: []}
      }

      const feedbacksFormat = feedbackConfigByType[instance.typeCode]
      const lines: SubactivityParamLine[] = []
      const groups: SubactivityParamGroup[] = [];
      (instance.feedbacks ?? []).forEach((feedback: any) => {
        if (feedback?.type === 'complex-type') {
          const complexFormat = feedbacksFormat?.find((format: any) => format.id === feedback.id)
          const groupLines = buildComplexParamLines(feedback, complexFormat)
          if (groupLines.length > 0) {
            groups.push({label: complexFormat?.label, lines: groupLines})
          }
          return
        }
        lines.push(...buildSimpleParamLines(feedback, feedbacksFormat))
      })

      return {id, title: instance.title, isOk, lines, groups}
    })
}

/**
 * Resolves an operator's qualification ids to a comma-separated list of
 * human-readable names via the qualifications catalog. Falls back to the
 * embedded `.name` when an entry is already an object rather than an id.
 */
export function resolveQualificationNames(
  qualifications?: any[],
  qualificationsById: Record<string, string> = {}
): string | undefined {
  if (!qualifications || qualifications.length === 0) return undefined
  const names = qualifications
    .map((q) => (typeof q === 'string' ? qualificationsById[q] ?? null : q?.name ?? null))
    .filter(Boolean)
  return names.length > 0 ? names.join(', ') : undefined
}

/**
 * Builds an id → name lookup from the qualifications catalog. Keys on
 * `_id` when present, falling back to `id` (the mock fixture only has `id`).
 */
export function buildQualificationsById(list?: Record<string, any>[]): Record<string, string> {
  return Object.fromEntries((list ?? []).map((q) => [q._id ?? q.id, q.name]))
}

/**
 * The "Paziente" field should only be shown when the logged-in user is NOT
 * the activity's own beneficiary — i.e. when the activity belongs to someone
 * else (caregiver/operator viewing a patient's activity). Defaults to
 * showing the field when either id is unavailable, so we never hide
 * information we can't actually verify.
 */
export function shouldShowBeneficiary(
  loggedUserId?: string,
  activity?: Record<string, any>
): boolean {
  const beneficiaryId = activity?.beneficiary?._id ?? activity?.beneficiaryId
  if (!loggedUserId || !beneficiaryId) return true
  return loggedUserId !== beneficiaryId
}
